import { SongPreview } from '~/interfaces';

export type FilterCategoryId = 'playlist' | 'genre' | 'language' | 'duets' | 'played' | 'length';

export interface FilterOption {
  /** Empty string is always "Any" - the option that clears the category. */
  value: string;
  label: string;
  count?: number;
}

export const ANY_OPTION: FilterOption = { value: '', label: 'Any' };

export type DuetsFilter = 'duets' | 'solo';
export type PlayedFilter = 'played' | 'never';
export type LengthFilter = 'short' | 'medium' | 'long' | 'epic';

export const DUETS_OPTIONS: FilterOption[] = [
  ANY_OPTION,
  { value: 'duets', label: 'Duets only' },
  { value: 'solo', label: 'Solo only' },
];

export const PLAYED_OPTIONS: FilterOption[] = [
  ANY_OPTION,
  { value: 'played', label: 'Played before' },
  { value: 'never', label: 'Never played' },
];

/**
 * Four buckets rather than the obvious three, because three puts 79% of the catalogue in the
 * middle one and answers nothing. Split here it reads 796 / 2983 / 1814 / 507.
 */
export const LENGTH_BUCKETS: Record<LengthFilter, { label: string; from: number; to: number }> = {
  short: { label: 'Under 3 min', from: 0, to: 180 },
  medium: { label: '3 – 4 min', from: 180, to: 240 },
  long: { label: '4 – 5 min', from: 240, to: 300 },
  epic: { label: 'Over 5 min', from: 300, to: Infinity },
};

export const LENGTH_OPTIONS: FilterOption[] = [
  ANY_OPTION,
  ...Object.entries(LENGTH_BUCKETS).map(([value, { label }]) => ({ value, label })),
];

export const isLengthMatch = (song: Pick<SongPreview, 'duration'>, bucket: LengthFilter) => {
  // A song with no recorded duration belongs to no bucket - it is unknown, not zero seconds.
  if (song.duration === undefined) return false;
  const { from, to } = LENGTH_BUCKETS[bucket];

  return song.duration >= from && song.duration < to;
};

/**
 * How a category names a number of its picks - "3 genres". Once there are too many to list, the
 * count on its own is ambiguous: the header pills carry no category name of their own, so two
 * multi-valued categories both narrowed would put up two pills reading "3 selected".
 */
export const CATEGORY_PICK_LABELS: Partial<Record<FilterCategoryId, string>> = {
  genre: 'genres',
  language: 'languages',
  length: 'lengths',
};

export const CATEGORY_LABELS: Record<FilterCategoryId, string> = {
  playlist: 'Playlist',
  genre: 'Genre',
  language: 'Language',
  duets: 'Duets',
  played: 'Played',
  length: 'Length',
};
